<template>
  <v-container class="contact-page" fluid>
    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Patient Services <span class="thai">บริการสำหรับผู้ป่วย</span></h1>
        <p class="subhead mb-0">Manage facility cards and categories for hospital websites.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name: 'facility-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Facility
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="category-bar" v-if="categoryOptions.length > 1">
        <button
          v-for="category in categoryOptions"
          :key="category"
          type="button"
          class="category-chip"
          :class="{'active': selectedCategory === category}"
          @click="onCategoryChange(category)"
        >
          {{ category }}
        </button>
      </div>

      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field
            v-model="searchText"
            append-icon="mdi-magnify"
            outlined
            dense
            hide-details
            label="Search by facility name or category..."
            clearable
            @input="onSearch"
          />
        </div>
        <v-select v-if="isSuperAdmin"
          v-model="selectedOrg"
          :items="orgOptionsWithAll"
          outlined
          dense
          hide-details
          item-text="text"
          item-value="value"
          class="small-select"
          :disabled="isBranchLocked"
          placeholder="All Branches"
          @input="onFilterChange"
        />
        <v-btn outlined class="filter-btn" :class="{'has-filtered': hasAppliedFilters}" @click="handleFilterButtonClick">
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table
        :loading="isPageLoading"
        :headers="visibleHeaders"
        :items="filteredItems"
        :items-per-page="pageSize"
        hide-default-footer
        class="contact-table"
      >
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>
        <template v-slot:item.category="{item}"><span class="type-pill">{{ item.category || '-' }}</span></template>
        <template v-slot:item.facility_name="{item}">
          <div class="name-cell">
            <div class="main">{{ item.name_en || '-' }}</div>
            <div class="sub">{{ item.name_th || '-' }}</div>
          </div>
        </template>
        <template v-slot:item.branch="{item}">
          <span class="branch-pill">{{ item.org_codename || '-' }}</span>
        </template>
        <template v-slot:item.location="{item}">{{ formatLocation(item) }}</template>
        <template v-slot:item.hours="{item}">{{ formatHours(item) }}</template>
        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot">
        <span>Showing {{ filteredItems.length }} to {{ filteredItems.length }} of {{ filteredItems.length }} entries</span>
      </div>
    </v-card>

  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      headers: [
        {text: 'NO.', value: 'no', sortable: false, width: 60},
        {text: 'CATEGORY', value: 'category', sortable: false, width: 180},
        {text: 'FACILITY NAME', value: 'facility_name', sortable: false},
        {text: 'BRANCH', value: 'branch', sortable: false, width: 120},
        {text: 'LOCATION', value: 'location', sortable: false, width: 160},
        {text: 'OPERATING HOURS', value: 'hours', sortable: false, width: 180},
        {text: 'ACTIONS', value: 'action', sortable: false, width: 120, align: 'center'}
      ],
      breadcrumbs: [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Patient Services', disabled: true, href: '/patient-services/facilities'},
        {text: 'Facilities', disabled: true, href: '/patient-services/facilities'}
      ],
      searchText: '',
      selectedOrg: '',
      selectedCategory: 'All',
      localLoading: false
    }
  },
computed: {
    ...mapState('facilities', ['list', 'isLoading', 'pageSize', 'orgOptions']),
    ...mapState('auth', ['user']),
    canAdd() { return hasPermision('CanAddFacility') },
    canEdit() { return hasPermision('CanEditFacility') },
    canDelete() { return hasPermision('CanDeleteFacility') },
    isBranchLocked() { return !this.isSuperAdmin || this.orgOptions.length === 1 },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    orgOptionsWithAll() {
      return [{text: 'All Branches', value: ''}, ...this.orgOptions.filter(item => item && item.value !== '')]
    },
    categoryOptions() {
      return ['All', 'Food & Drink', 'Convenience', 'Services & Lounges']
    },
    visibleHeaders() {
      if (this.isSuperAdmin) return this.headers
      return this.headers.filter(item => item.value !== 'branch')
    },
    hasAppliedFilters() {
      const hasSearch = !!(this.searchText || '').trim()
      const hasBranchFilter = this.isSuperAdmin && !!(this.selectedOrg || '').trim()
      const hasCategoryFilter = this.selectedCategory !== 'All'
      return hasSearch || hasBranchFilter || hasCategoryFilter
    },
    filteredItems() {
      const userOrg = this.getUserOrgCode()
      return this.list.filter(item => {
        if (this.isSuperAdmin) {
          if (this.selectedOrg && item.org_codename !== this.selectedOrg) return false
        } else if (userOrg && item.org_codename !== userOrg) {
          return false
        }
        if (this.selectedCategory !== 'All' && (item.category || '') !== this.selectedCategory) return false
        const term = (this.searchText || '').toLowerCase().trim()
        if (!term) return true
        return `${item.name_en || ''} ${item.name_th || ''} ${item.category || ''}`.toLowerCase().includes(term)
      })
    },
    isPageLoading() {
      return this.localLoading || this.isLoading
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.resetList()
      this.setResource('facilities')
      await this.fetchOrgOptions()
      if (!this.isSuperAdmin) {
        this.selectedOrg = this.getUserOrgCode()
        this.setFilterOrgCode(this.selectedOrg)
      } else if (this.orgOptions.length === 1) {
        this.selectedOrg = this.orgOptions[0].value
        this.setFilterOrgCode(this.selectedOrg)
      }
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('facilities', ['resetList', 'setResource', 'fetch', 'deleteById', 'setTextSearch', 'setFilterOrgCode', 'fetchOrgOptions']),
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const sourceItems = Array.isArray(this.filteredItems) ? this.filteredItems : this.list
        const fallbackIndex = (sourceItems || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    getUserOrgCode() {
      const user = this.user || {}
      return user.org_code || user.org_codename || user.org || ''
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onFilterChange() {
      this.localLoading = true
      try {
        this.setFilterOrgCode(this.selectedOrg || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async handleFilterButtonClick() {
      if (!this.hasAppliedFilters) return

      this.localLoading = true
      try {
        this.searchText = ''
        this.selectedCategory = 'All'
        this.selectedOrg = this.isSuperAdmin ? '' : this.getUserOrgCode()
        this.setTextSearch('')
        this.setFilterOrgCode(this.selectedOrg)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    onCategoryChange(category) {
      this.selectedCategory = category
    },
    goToEdit(item) {
      if (!item || !item.id) return
      this.$router.push({name: 'facility-edit', params: {id: item.id}})
    },
    formatLocation(item) {
      const building = (item.building || '').trim()
      const floor = (item.floor || '').trim()
      if (building && floor) return `${building}, ${floor}`
      return building || floor || '-'
    },
    formatHours(item) {
      const openTime = (item.open_time || '').trim()
      const closeTime = (item.close_time || '').trim()
      if (openTime && closeTime) return `${openTime} – ${closeTime}`
      return openTime || closeTime || '-'
    },
    async remove(item) {
      if (!window.confirm('Delete this facility?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if (ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #7c8aa5; font-size: 18px; }
.subhead { color: #6d7f9e; font-size: 14px; }
.setup-btn { text-transform: none; font-weight: 700; border-radius: 10px; }
.table-card { border: 1px solid #e4ebf7; border-radius: 12px; box-shadow: 0 1px 8px rgba(16,35,72,.05); }
.category-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; flex-wrap: wrap; }
.category-chip { border: 1px solid #dfe7f6; background: #fff; color: #5f7397; border-radius: 999px; padding: 8px 18px; font-size: 14px; font-weight: 700; cursor: pointer; }
.category-chip.active { background: #eef4ff; border-color: #bcd3ff; color: #1e49ba; }
.filter-bar { display: flex; gap: 10px; padding: 14px; border-bottom: 1px solid #edf2fb; }
.search-wrap { flex: 5; }
.small-select { flex: 1; }
.filter-btn { text-transform: none; font-weight: 600; min-width: 96px; }
.filter-btn.has-filtered { border-color: #f3b5b5 !important; color: #c62828 !important; background: #fdecec; }
.contact-table::v-deep .v-data-table-header th { color: #7c8aa5 !important; font-size: 12px; font-weight: 700; }
.name-cell .main { font-weight: 700; color: #14274a; }
.name-cell .sub { color: #7c8aa5; font-size: 12px; }
.type-pill { background: #eef4ff; color: #1e49ba; border-radius: 8px; padding: 4px 9px; font-weight: 700; font-size: 12px; }
.branch-pill { display:inline-flex; align-items:center; font-size:11.5px; font-weight:700; letter-spacing:.2px; padding:4px 9px; border-radius:6px; background:#eef1f6; color:#5b6472; }
.table-foot { padding: 12px 14px; border-top: 1px solid #edf2fb; color: #7c8aa5; font-size: 13px; }
.icon-btn { border: 1px solid #dce6f8; border-radius: 8px; width: 30px; height: 30px; background: #fff; }
</style>
